import {
  all,
  AllEffect,
  call,
  CallEffect,
  delay,
  ForkEffect,
  race,
  RaceEffect,
  take,
  TakeEffect,
  takeLatest,
} from 'redux-saga/effects';
import { Analytics } from 'b2b-apps/core/analytics/helpers/Analytics';
import {
  AnalyzeProductAddToWishlistAction,
  analyzeScannedProductAddToCart,
  analyzeScannedProductAddToWishlist,
  analyzeScannedProductClick,
} from 'b2b-apps/core/analytics/redux';
import { ADD_TO_CART_FULFILLED, ADD_TO_CART_REJECTED } from 'b2b-apps/core/cart/redux/addToCart/actionTypes';
import { AddToCartFulfilledAction, AddToCartRejectedAction } from 'b2b-apps/core/cart/redux/addToCart/types';
import { ADD_ITEMS_TO_CART_FULFILLED, ADD_ITEMS_TO_CART_REJECTED } from 'b2b-apps/core/cart/redux/cart/actionTypes';
import { AddItemsToCartActionFulfilled, AddItemsToCartActionRejected } from 'b2b-apps/core/cart/redux/cart/types';
import { EVENT_SOURCE } from 'b2b-apps/shop/analytics/types/types';
import {
  analyzeSearchProductAddToCartInteraction as analyzeSearchProductAddToCart,
  analyzeSearchProductClickInteraction as analyzeSearchProductClick,
} from '../search/interactions';
import {
  ANALYZE_PRODUCT_ADD_TO_CART,
  ANALYZE_PRODUCT_CLICK,
  ANALYZE_PRODUCT_DETAILS_VIEW,
  ANALYZE_PRODUCT_WISHLIST_ADD,
  ANALYZE_PRODUCTS_ADD_TO_CART,
  ANALYZE_PRODUCTS_LIST_VIEW,
} from './actionTypes';
import {
  analyzeAccessoryProductAddToCart,
  analyzeAccessoryProductClick,
  analyzeBrandProductAddToCart,
  analyzeBrandProductClick,
  analyzeBrandProductListView,
  analyzeCatalogProductListView,
  analyzeCategoryProductAddToCart,
  analyzeCategoryProductClick,
  analyzeCMSProductAddToCart,
  analyzeCMSProductClick,
  analyzeCmsProductsListView,
  analyzeComparisonProductAddToCart,
  analyzeComparisonProductClick,
  analyzeProductAccessoriesListView,
  analyzeWishlistProductAddToCart,
  analyzeWishlistProductClick,
  analyzeWishlistProductsAddToCart,
} from './lists';
import { analyzePDPProductAddToCart, analyzeProductDetailsPageViewSaga } from './product';
import {
  AnalyzeProductAddToCartAction,
  AnalyzeProductClickAction,
  AnalyzeProductsAddToCartAction,
  AnalyzeProductsListViewAction,
} from './types';

function* analyzeProductsListSaga({ payload: { eventSource, skus } }: AnalyzeProductsListViewAction):
  Generator<CallEffect, void> {
  //TODO Temporary fix to delay EC-PLI events after page view is processed
  yield delay(Analytics.delayTealiumPageView);

  switch (eventSource) {
    case EVENT_SOURCE.category:
      yield call(analyzeCatalogProductListView);
      break;
    case EVENT_SOURCE.brand:
      yield call(analyzeBrandProductListView);
      break;
    case EVENT_SOURCE.accessories:
      yield call(analyzeProductAccessoriesListView, skus[0]);
      break;
    case EVENT_SOURCE.cms:
      yield call(analyzeCmsProductsListView, skus);
      break;
    default:
      break;
  }
}

function* analyzeProductClickSaga(action: AnalyzeProductClickAction):
  Generator<CallEffect, void> {
  const { eventSource, sku, listPosition, additionalId } = action.payload;

  switch (eventSource) {
    case EVENT_SOURCE.category:
      yield call(analyzeCategoryProductClick, sku, listPosition);
      break;
    case EVENT_SOURCE.search:
      yield call(analyzeSearchProductClick, sku);
      break;
    case EVENT_SOURCE.brand:
      yield call(analyzeBrandProductClick, sku, listPosition);
      break;
    case EVENT_SOURCE.cms:
      yield call(analyzeCMSProductClick, sku, listPosition);
      break;
    case EVENT_SOURCE.accessories:
      yield call(analyzeAccessoryProductClick, sku, additionalId as string, listPosition);
      break;
    case EVENT_SOURCE.wishlist:
      yield call(analyzeWishlistProductClick, sku, additionalId as string, listPosition);
      break;
    case EVENT_SOURCE.comparison:
      yield call(analyzeComparisonProductClick, sku, listPosition);
      break;
    case EVENT_SOURCE.barcode:
      yield call(analyzeScannedProductClick, sku, additionalId as string);
      break;
    // TODO: enable after https://competecjira.atlassian.net/browse/ALL3-5722
    // case EVENT_SOURCE.deliveredOrder:
    //   yield call(analyzeDeliveredOrderProductClick, sku, additionalId as number, listPosition);
    //   break;
    default:
      break;
  }
}

function* analyzeProductAddToCartSaga(action: AnalyzeProductAddToCartAction):
  Generator<
    CallEffect | RaceEffect<TakeEffect>,
    void,
    { fulfilled: AddToCartFulfilledAction, rejected: AddToCartRejectedAction }
  > {
  const { eventSource, sku, listPosition, additionalId } = action.payload;
  const { fulfilled, rejected } = yield race({
    fulfilled: take(ADD_TO_CART_FULFILLED),
    rejected: take(ADD_TO_CART_REJECTED),
  });

  if (rejected) {
    return;
  }

  const { quantity } = fulfilled.payload;

  switch (eventSource) {
    case EVENT_SOURCE.category:
      yield call(analyzeCategoryProductAddToCart, sku, listPosition);
      break;
    case EVENT_SOURCE.search:
      yield call(analyzeSearchProductAddToCart, sku);
      break;
    case EVENT_SOURCE.brand:
      yield call(analyzeBrandProductAddToCart, sku, listPosition);
      break;
    case EVENT_SOURCE.cms:
      yield call(analyzeCMSProductAddToCart, sku, listPosition);
      break;
    case EVENT_SOURCE.accessories:
      yield call(analyzeAccessoryProductAddToCart, sku, additionalId as string, listPosition);
      break;
    case EVENT_SOURCE.pdp:
      yield call(analyzePDPProductAddToCart, sku, quantity);
      break;
    case EVENT_SOURCE.wishlist:
      yield call(analyzeWishlistProductAddToCart, sku, additionalId as string, listPosition, quantity);
      break;
    case EVENT_SOURCE.comparison:
      yield call(analyzeComparisonProductAddToCart, sku, listPosition);
      break;
    case EVENT_SOURCE.barcode:
      yield call(analyzeScannedProductAddToCart, sku);
      break;
    // TODO: enable after https://competecjira.atlassian.net/browse/ALL3-5722
    // case EVENT_SOURCE.deliveredOrder:
    //   yield call(analyzeDeliveredOrderProductAddToCart, sku, additionalId as number, listPosition, quantity);
    //   break;
    default:
      break;
  }
}

function* analyzeProductsAddToCartSaga(action: AnalyzeProductsAddToCartAction):
  Generator<
    CallEffect | RaceEffect<TakeEffect>,
    void,
    { fulfilled: AddItemsToCartActionFulfilled, rejected: AddItemsToCartActionRejected }
  > {
  const { eventSource, products, additionalId } = action.payload;
  const { fulfilled, rejected } = yield race({
    fulfilled: take(ADD_ITEMS_TO_CART_FULFILLED),
    rejected: take(ADD_ITEMS_TO_CART_REJECTED),
  });

  if (rejected) {
    return;
  }

  const addedProducts = fulfilled.payload.data.added;

  if (!addedProducts.length) {
    return;
  }

  const mergedProducts = addedProducts?.reduce(
    (allProducts, addedProduct, index) => {
      const matchingProduct = products?.find(product => product.sku === addedProduct.sku) || null;

      return [
        ...allProducts,
        {
          sku: addedProduct.sku,
          quantity: addedProduct.quantity,
          listPosition: matchingProduct?.listPosition || index + 1,
        },
      ];
    },
    [],
  ) || [];

  switch (eventSource) {
    case EVENT_SOURCE.wishlist:
      yield call(analyzeWishlistProductsAddToCart, mergedProducts, additionalId as string);
      break;
    // TODO: enable after https://competecjira.atlassian.net/browse/ALL3-5722
    // case EVENT_SOURCE.deliveredOrder:
    //   yield call(analyzeDeliveredOrderProductsAddToCart, mergedProducts, additionalId as number);
    //   break;
    default:
      break;
  }
}

function* analyzeProductWishlistAddSaga(action:
  AnalyzeProductAddToWishlistAction): Generator<CallEffect | TakeEffect, void > {
  const { eventSource, sku } = action.payload;

  switch (eventSource) {
    case EVENT_SOURCE.barcode:
      yield call(analyzeScannedProductAddToWishlist, sku);
      break;
    default:
      break;
  }
}

export function* productsAnalyticsSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(ANALYZE_PRODUCT_DETAILS_VIEW, analyzeProductDetailsPageViewSaga),
    takeLatest(ANALYZE_PRODUCTS_LIST_VIEW, analyzeProductsListSaga),
    takeLatest(ANALYZE_PRODUCT_CLICK, analyzeProductClickSaga),
    takeLatest(ANALYZE_PRODUCT_ADD_TO_CART, analyzeProductAddToCartSaga),
    takeLatest(ANALYZE_PRODUCTS_ADD_TO_CART, analyzeProductsAddToCartSaga),
    takeLatest(ANALYZE_PRODUCT_WISHLIST_ADD, analyzeProductWishlistAddSaga),
  ]);
}
